import {useCallback, useContext, useEffect, useState} from "react";
import {Box, Button, Grid, TextField, Typography} from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {NotifyContext} from "../../../contexts/notifyContext";
import {Formik} from "formik";
import {useStripePaymentMethod, useUpdatePaymentMethod} from "../../../graphql/graphql/stripe/hooks";

const inputStyle = {
  color: "rgba(255, 255, 255, 0.38)",
  fontWeight: "400",
  fontFamily: '"Inter", sans-serif',
  fontSize: "16px",
  ":-webkit-autofill": {
    color: "#fce883",
  },
  "::placeholder": {
    color: "rgba(255, 255, 255, 0.38)",
  },
};

const SaveNewCard = ({open, setOpen, cards, setShowAlert}) => {
  const elements = useElements();
  const stripe = useStripe();
  // const [name, setName] = useState("");
  const [disabled, setDisabled] = useState();
  const [cardEmptyCheck, setCardEmptyCheck] = useState({cardNumber: true, cardExpiry: true, cardCvc: true});
  const [currentCard, setCurrentCard] = useState({name: ""});
  const {toast} = useContext(NotifyContext);
  const {updatePaymentMethod, addPaymentMethod} = useStripePaymentMethod();

  useEffect(() => {
    if (open !== "create") {
      const currentCard = cards?.find((card) => card.id === open);
      const {exp_month, exp_year} = currentCard?.card;
      setCurrentCard({...currentCard, exp_month, exp_year, name: currentCard?.billing_details?.name});
    } else {
      setCurrentCard({...currentCard, name: ""});
    }
  }, [open]);

  const handleCardChange = async (event) => {
    setCardEmptyCheck({...cardEmptyCheck, [event.elementType]: event.error ? true : event.empty});
  };

  const onAddCard = async (currentCard) => {
    try {
      if (open === "create") {
        const {error, paymentMethod} = await stripe.createPaymentMethod({
          type: "card",
          billing_details: {name: currentCard?.name},
          card: elements.getElement(CardNumberElement),
        });
        if (error) return;
        // await axiosClient.post(`/user/stripe/payment_methods/attech/${paymentMethod.id}`);
        await addPaymentMethod({
          variables: {
            input: {
              slug: paymentMethod.id,
            },
          },
        });

        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
        }, 5000);
      } else {
        const {exp_month, exp_year} = currentCard;
        await updatePaymentMethod({
          variables: {input: {name: currentCard?.name, exp_month, exp_year, paymentMethodId: open}},
        });
        // const updateCardInfo = await axiosClient.put(`/user/stripe/payment_method`, {
        //   name: currentCard?.name,
        //   exp_month,
        //   exp_year,
        //   paymentMethodId: open,
        // });
        // if (!updateCardInfo?.error) {
        //   toast({type: "success", message: updateCardInfo?.message});
        // }
      }
      // await getMyCards();
      setOpen(false);
    } catch (error) {
      toast({type: "error", message: error?.message});
    }
  };

  const validate = async (values) => {
    const errors = {};
    if (!values.name) {
      errors.name = "Required";
    }
    if (open !== "create") {
      if (!values.exp_month) {
        errors.exp_month = "Required";
      }
      if (!values.exp_year) {
        errors.exp_year = "Required";
      }
      if (values.exp_month || values.exp_year) {
        const {exp_month, exp_year} = values;
        if (!(new Date().getMonth() + 1 <= exp_month && exp_month < 13)) {
          errors.exp_month = "Entered month is invalid";
        } else if (!(new Date().getFullYear() <= exp_year)) {
          errors.exp_year = "Entered Year is invalid";
        }
      }
    }
    return errors;
  };

  const handleDisabled = useCallback(
    (isValid, dirty) => {
      if (open === "create") {
        return !(isValid && dirty) || cardEmptyCheck.cardCvc || cardEmptyCheck.cardNumber || cardEmptyCheck.cardExpiry;
      } else {
        return !(isValid && dirty);
      }
    },
    [cardEmptyCheck.cardCvc, cardEmptyCheck.cardNumber, cardEmptyCheck.cardExpiry]
  );

  const renderErrorIcon = (error) => {
    return {
      endAdornment: error && <ErrorOutlineOutlinedIcon color="error" />,
    };
  };

  return (
    <Formik
      initialValues={currentCard}
      enableReinitialize
      validate={(values) => validate(values)}
      onSubmit={(values, {setSubmitting}) => onAddCard(values, setSubmitting)}
    >
      {({values, errors, handleChange, handleBlur, handleSubmit, isValid, dirty, setFieldValue}) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="modalForm"
        >
          <Grid container mb={2} spacing={2}>
            <Grid item xs={open === "create" ? 8 : 12}>
              <Typography variant="h5">Name on card</Typography>
              <TextField
                fullWidth
                className={errors?.name && "error-input"}
                type="text"
                required
                placeholder="Name on card"
                name="name"
                value={values?.name}
                onChange={handleChange}
                onBlur={handleBlur}
                InputProps={renderErrorIcon(errors?.name)}
              />
            </Grid>
            {open === "create" ? (
              <>
                <Grid item xs={4}>
                  <Typography variant="h5">Expiry</Typography>
                  <Box className="inputField">
                    <CardExpiryElement
                      onChange={handleCardChange}
                        options={{style: {base: inputStyle}, showIcon: true}}
                    />
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="h5">Card number</Typography>
                  <Box className="inputField">
                    <CardNumberElement
                      onChange={handleCardChange}
                      options={{style: {base: inputStyle}, showIcon: true}}
                    />
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h5">CVV</Typography>
                  <Box className="inputField">
                    <CardCvcElement onChange={handleCardChange} options={{style: {base: inputStyle}, showIcon: true}} />
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={6}>
                  <Typography variant="h5">Expiry Month</Typography>
                  <TextField
                    min={0}
                    className={errors?.exp_month && "error-input"}
                    onInput={(e) => {
                      e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 2);
                    }}
                    fullWidth
                    type="number"
                    value={values?.exp_month}
                    required
                    placeholder="Month"
                    name="exp_month"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    InputProps={renderErrorIcon(errors?.exp_month)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h5">Expiry Year</Typography>
                  <TextField
                    className={errors?.exp_year && "error-input"}
                    min={0}
                    onInput={(e) => {
                      e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 4);
                    }}
                    fullWidth
                    type="number"
                    value={values?.exp_year}
                    required
                    placeholder="Year"
                    name="exp_year"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    InputProps={renderErrorIcon(errors?.exp_year)}
                  />
                </Grid>
              </>
            )}
          </Grid>
          <Grid container pt={2} spacing={2}>
            <Grid item xs={6}>
              <Button fullWidth onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button fullWidth type="submit" disabled={handleDisabled(isValid, dirty)} className="btnSave">
                {open === "create" ? "Create" : "Update"}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

export default SaveNewCard;
